import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");
  const [userEmail, setUserEmail] = useState(""); // Store user email from response
  const navigate = useNavigate();
  const hasCalledApi = useRef(false); // Prevent double API call in Strict Mode

  const inviteCode = searchParams.get("code");

  useEffect(() => {
    const acceptInvitation = async () => {
      // Prevent double call in React Strict Mode
      if (hasCalledApi.current) return;
      hasCalledApi.current = true;

      if (!inviteCode) {
        setStatus("error");
        setMessage("Invalid invitation link. No invite code provided.");
        return;
      }

      try {
        const response = await fetch(`/api/organization/accept-invite?code=${inviteCode}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        const data = await response.json();

        if (data.success) {
          setStatus("success");
          setMessage(data.message || "Invitation accepted successfully! You can now login.");
          // Store email and password from response (check both newBody and data for compatibility)
          const email = data.newBody?.email || data.data?.email;
          const tempPassword = data.newBody?.temporaryPassword || data.data?.temporaryPassword;
          if (email) {
            setUserEmail(email);
          }
          // Store password in state for passing to signin page
          if (tempPassword) {
            navigate("/sign-in-sso", { 
              state: { 
                prefillEmail: email,
                prefillPassword: tempPassword 
              } 
            });
            return; // Skip manual button click
          }
        } else {
          setStatus("error");
          setMessage(data.message || "Failed to accept invitation.");
        }
      } catch (error) {
        console.error("Error accepting invitation:", error);
        setStatus("error");
        setMessage("An error occurred while accepting the invitation.");
      }
    };

    acceptInvitation();
  }, [inviteCode]);

  const handleGoToLogin = () => {
    // Navigate to signin with email pre-filled via state
    // Note: Password is auto-navigated in success handler above
    navigate("/sign-in-sso", { state: { prefillEmail: userEmail } });
  };

  return (
    <div
      className="h-screen flex justify-center items-center"
      style={{
        background: "linear-gradient(180deg, #F4F5F6 12.73%, #C9E4FF 80%)",
      }}
    >
      <div className="bg-white p-10 rounded-3xl shadow-lg w-[90%] max-w-[450px]">
        {status === "loading" && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <h2 className="text-xl font-medium text-gray-700">
              Processing your invitation...
            </h2>
          </div>
        )}

        {status === "success" && (
          <div className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-medium text-gray-800 mb-2">
              Welcome to the Team!
            </h2>
            <p className="text-gray-600 mb-4">{message}</p>
            
            {userEmail && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-gray-700 mb-2">
                  <span className="font-medium">Your login email:</span>
                </p>
                <p className="text-sm font-mono bg-white px-3 py-2 rounded border">
                  {userEmail}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Check your email for login credentials. If you haven't received the email, 
                  contact your organization admin.
                </p>
              </div>
            )}
            
            <button
              onClick={handleGoToLogin}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Go to Login
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-medium text-gray-800 mb-2">
              Invitation Error
            </h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={handleGoToLogin}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
